import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  CurrencyDropDown,
  DocumentType,
  EntryType,
  Invoice,
  InvoiceExpenseType
} from 'openapi';

import { Modal } from 'components/shared/Modal/Modal';

import { useTranslations } from 'context/TranslationContext';

import { DocumentConfig } from 'utils/config/documentEntries';
import {
  CONFIDENCE_STATUS_COLORS,
  getConfidenceFieldSx
} from 'utils/constants/confidenceStatusColors';
import { DATE_FORMATS } from 'utils/constants/constants';
import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import { ConfidenceLevel } from 'utils/enums/Confidence';
import { getFieldConfidenceUi } from 'utils/helpers/confidenceHelpers';
import { validateDateValue } from 'utils/helpers/dates';
import { getDocumentDataSummary } from 'utils/helpers/invoiceSectionSummaryHelpers';
import { processMoneyInput } from 'utils/helpers/moneyHelper';
import { validateDocumentNumber } from 'utils/helpers/validationHelpers';

import {
  modalButtonsWrapper,
  verificationFormFields,
  verificationFormFieldsWrapper,
  verificationFormItem,
  verificationFormItemNoHeight
} from 'styles/pages/InvoiceVerificationStyle';

import { InvoiceVerificationCard } from './InvoiceVerificationCard';

const formatToTwoDecimals = (value: string | number | null | undefined) => {
  if (value === undefined || value === null || value === '') {
    return '0.00';
  }
  return Number(value).toFixed(2);
};

interface DocumentDataCardProps {
  currentInvoice: Invoice;
  expenseTypeOptions: InvoiceExpenseType[];
  currencies: CurrencyDropDown[];
  documentConfig: DocumentConfig;
  confidenceLevel?: ConfidenceLevel;
  expanded?: boolean;
  onToggle?: () => void;
  onExpanded?: () => void;
  autoExpandReason?: string;
  hasError?: boolean;
  onExpenseTypeChange?: (
    expenseTypeId: number | string | null | undefined
  ) => void;
  /**
   * EPN-607: called after the user confirms an expense type change in the
   * confirmation dialog, so the parent can persist it immediately (the
   * server cascades the new type to every line item).
   */
  onExpenseTypeApplied?: (expenseTypeId: number) => void;
}

export const DocumentDataCard = ({
  currentInvoice,
  expenseTypeOptions,
  currencies,
  documentConfig,
  confidenceLevel,
  expanded,
  onToggle,
  onExpanded,
  autoExpandReason,
  hasError,
  onExpenseTypeChange,
  onExpenseTypeApplied
}: DocumentDataCardProps) => {
  const { translate } = useTranslations();
  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { touchedFields }
  } = useFormContext();

  const [isInvoiceDateOpen, setInvoiceDateOpen] = useState(false);
  const [isDueDateOpen, setDueDateOpen] = useState(false);
  // EPN-607: an expense type selection that differs from the current one is
  // held here until the user confirms the bulk line-item update; the form
  // value stays untouched until then (Cancel leaves nothing changed).
  const [pendingExpenseTypeChange, setPendingExpenseTypeChange] = useState<
    string | number | null
  >(null);

  useEffect(() => {
    const amountFields = [
      InvoiceVerificationFieldName.VatBase,
      InvoiceVerificationFieldName.VatAmount,
      InvoiceVerificationFieldName.InvoiceAmount
    ] as const;
    amountFields.forEach((field) => {
      const value = getValues(field);
      if (value !== undefined && value !== null && value !== '') {
        setValue(field, formatToTwoDecimals(value), {
          shouldValidate: false,
          shouldDirty: false
        });
      }
    });
  }, [getValues, setValue]);

  const watchedInvoiceNumber = watch(
    InvoiceVerificationFieldName.InvoiceNumber
  );
  const watchedVatBase = watch(InvoiceVerificationFieldName.VatBase);
  const watchedVatAmount = watch(InvoiceVerificationFieldName.VatAmount);
  const watchedInvoiceAmount = watch(
    InvoiceVerificationFieldName.InvoiceAmount
  );
  const watchedInvoiceDate = watch(InvoiceVerificationFieldName.InvoiceDate);
  const watchedCurrencyId = watch(InvoiceVerificationFieldName.CurrencyId);
  const watchedExpenseType = watch(InvoiceVerificationFieldName.ExpenseType);

  // Report expense type selections live so other cards can react before the
  // value is persisted on Send for Approval. The ref is seeded with the value
  // seen on the first render, so the unchanged initial value never fires.
  const previousExpenseTypeRef = useRef(watchedExpenseType);

  useEffect(() => {
    if (previousExpenseTypeRef.current === watchedExpenseType) {
      return;
    }
    previousExpenseTypeRef.current = watchedExpenseType;
    onExpenseTypeChange?.(watchedExpenseType);
  }, [watchedExpenseType, onExpenseTypeChange]);

  // EPN-607: on confirm the form value is written (which also reports the
  // live change) and the parent persists it immediately; the server cascades
  // the new type to every line item. Cancel writes nothing.
  const handleConfirmExpenseTypeChange = () => {
    const pending = pendingExpenseTypeChange;
    setPendingExpenseTypeChange(null);
    if (pending == null || pending === '') {
      return;
    }
    setValue(InvoiceVerificationFieldName.ExpenseType, Number(pending), {
      shouldValidate: true,
      shouldDirty: true
    });
    onExpenseTypeApplied?.(Number(pending));
  };

  const summary = useMemo(
    () =>
      getDocumentDataSummary(
        {
          invoiceNumber: watchedInvoiceNumber,
          invoiceDate: watchedInvoiceDate,
          expenseType: watchedExpenseType,
          invoiceAmount: watchedInvoiceAmount,
          currencyId: watchedCurrencyId,
          expenseTypeOptions,
          currencies
        },
        translate
      ),
    [
      watchedInvoiceNumber,
      watchedInvoiceDate,
      watchedExpenseType,
      watchedInvoiceAmount,
      watchedCurrencyId,
      expenseTypeOptions,
      currencies,
      translate
    ]
  );

  const BGN_RESTRICTION_DATE = moment('2026-01-01', 'YYYY-MM-DD');

  const isDateRestricted = (invoiceDateValue: string): boolean => {
    if (!invoiceDateValue) return false;
    const date = moment(
      invoiceDateValue,
      DATE_FORMATS.displayedDateFormat,
      true
    );
    return date.isValid() && date.isSameOrAfter(BGN_RESTRICTION_DATE, 'day');
  };

  const filteredCurrencies = useMemo(
    () =>
      currencies.filter((currency: CurrencyDropDown) => {
        if (!isDateRestricted(String(watchedInvoiceDate))) return true;
        return currency.isoCode !== 'BGN';
      }),
    [currencies, watchedInvoiceDate]
  );

  const isBgnSelected = useMemo(() => {
    if (!watchedCurrencyId) return false;
    const selectedCurrency = currencies.find((c) => c.id === watchedCurrencyId);
    return selectedCurrency?.isoCode === 'BGN';
  }, [watchedCurrencyId, currencies]);

  useEffect(() => {
    if (
      watchedInvoiceDate &&
      isDateRestricted(String(watchedInvoiceDate)) &&
      isBgnSelected
    ) {
      setValue(InvoiceVerificationFieldName.CurrencyId, '');
    }
  }, [watchedInvoiceDate, watchedCurrencyId, setValue, isBgnSelected]);

  const isInvoiceAmountValid = useMemo(() => {
    if (!watchedInvoiceAmount) {
      return false;
    }
    const total =
      (Number(watchedVatBase) || 0) + (Number(watchedVatAmount) || 0);
    return (
      (Number(watchedInvoiceAmount) || 0).toFixed(2) ===
      Number(total).toFixed(2)
    );
  }, [watchedVatBase, watchedVatAmount, watchedInvoiceAmount]);

  const validateInvoiceAmount = (value?: string | number) => {
    const amountToCheck =
      value ?? getValues(InvoiceVerificationFieldName.InvoiceAmount);
    if (!amountToCheck) {
      return true;
    }
    const total =
      (Number(getValues(InvoiceVerificationFieldName.VatBase)) || 0) +
      (Number(getValues(InvoiceVerificationFieldName.VatAmount)) || 0);
    return (Number(amountToCheck) || 0).toFixed(2) === total.toFixed(2)
      ? true
      : 'errors.invoiceAmountEqual';
  };

  const isRevenue = currentInvoice.entryType === EntryType.REVENUE;
  const isNoDocumentType =
    currentInvoice.documentType === DocumentType.NO_DOCUMENT;

  const areAmountFieldsTouched =
    touchedFields[InvoiceVerificationFieldName.VatBase] ||
    touchedFields[InvoiceVerificationFieldName.VatAmount] ||
    touchedFields[InvoiceVerificationFieldName.InvoiceAmount];

  const shouldShowInvoiceAmountError =
    !isInvoiceAmountValid && areAmountFieldsTouched;

  return (
    <InvoiceVerificationCard
      title={translate('labels.documentData')}
      summary={summary}
      confidenceLevel={confidenceLevel}
      expanded={expanded}
      onToggle={onToggle}
      onExpanded={onExpanded}
      autoExpandReason={autoExpandReason}
      hasError={hasError}
    >
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box sx={verificationFormFields}>
          {!isNoDocumentType && (
            <Box
              sx={{
                ...verificationFormFieldsWrapper,
                minHeight: '50px',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 3, sm: 2 },
                mb: { xs: 1, sm: 0 }
              }}
            >
              <FormControl sx={verificationFormItem}>
                <Controller
                  name={InvoiceVerificationFieldName.InvoiceNumber}
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => validateDocumentNumber(value)
                  }}
                  render={({ field, fieldState: { error, isDirty } }) => {
                    const { isLow, isMedium, confidenceMessage } =
                      getFieldConfidenceUi(
                        currentInvoice,
                        InvoiceVerificationFieldName.InvoiceNumber,
                        translate,
                        isDirty
                      );
                    const isEmpty = !field.value;
                    const hasRequiredError = !!error || isEmpty;
                    const confidenceTone = hasRequiredError
                      ? null
                      : isLow
                        ? 'lowConfidence'
                        : isMedium
                          ? 'needsReview'
                          : null;
                    const showError = hasRequiredError || isLow;
                    const isWarning = confidenceTone === 'needsReview';

                    return (
                      <TextField
                        {...field}
                        error={showError}
                        color={isWarning ? 'warning' : undefined}
                        label={`${translate('labels.invoiceNumber')}*`}
                        name={InvoiceVerificationFieldName.InvoiceNumber}
                        placeholder={translate('labels.invoiceNumber')}
                        sx={
                          confidenceTone
                            ? getConfidenceFieldSx(
                                verificationFormItem,
                                confidenceTone
                              )
                            : verificationFormItem
                        }
                        helperText={
                          hasRequiredError
                            ? translate(
                                error?.message
                                  ? String(error.message)
                                  : 'errors.required'
                              )
                            : (confidenceMessage?.text ?? '')
                        }
                        FormHelperTextProps={{
                          sx: {
                            color: confidenceTone
                              ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                              : undefined
                          }
                        }}
                      />
                    );
                  }}
                />
              </FormControl>

              <Controller
                name={InvoiceVerificationFieldName.InvoiceDate}
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    const date = moment(
                      value,
                      DATE_FORMATS.displayedDateFormat,
                      true
                    );
                    const dateValue = date.isValid() ? date.toDate() : null;
                    const error = validateDateValue(dateValue, false);
                    if (error) return error;
                    const dueDateValue = getValues(
                      InvoiceVerificationFieldName.DueDate
                    );
                    if (dueDateValue && date.isValid()) {
                      const dueDate = moment(
                        dueDateValue,
                        DATE_FORMATS.displayedDateFormat,
                        true
                      );
                      if (dueDate.isValid() && date.isAfter(dueDate, 'day')) {
                        return 'errors.invoiceDateAfterDueDate';
                      }
                    }
                    return true;
                  }
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error, isDirty }
                }) => {
                  const { isLow, isMedium, confidenceMessage } =
                    getFieldConfidenceUi(
                      currentInvoice,
                      InvoiceVerificationFieldName.InvoiceDate,
                      translate,
                      isDirty
                    );
                  const isEmpty = !value;
                  const hasRequiredError = !!error || isEmpty;
                  const confidenceTone = hasRequiredError
                    ? null
                    : isLow
                      ? 'lowConfidence'
                      : isMedium
                        ? 'needsReview'
                        : null;
                  const showError = hasRequiredError || isLow;
                  const isWarning = confidenceTone === 'needsReview';

                  return (
                    <FormControl
                      error={showError}
                      color={isWarning ? 'warning' : undefined}
                      sx={verificationFormItem}
                    >
                      <DatePicker
                        sx={{ width: '100%' }}
                        format={DATE_FORMATS.displayedDateFormat}
                        open={isInvoiceDateOpen}
                        label={`${translate('labels.invoiceDate')}*`}
                        value={
                          value
                            ? moment(value, DATE_FORMATS.displayedDateFormat)
                            : null
                        }
                        onClose={() => setInvoiceDateOpen(false)}
                        onChange={(value) => {
                          const dateValue = value as Moment | null;
                          if (!dateValue?.isValid()) {
                            onChange('');
                          } else {
                            onChange(
                              dateValue.format(DATE_FORMATS.displayedDateFormat)
                            );
                          }
                          void trigger(InvoiceVerificationFieldName.DueDate);
                        }}
                        slotProps={{
                          textField: {
                            error: showError,
                            color: isWarning ? 'warning' : undefined,
                            sx: confidenceTone
                              ? getConfidenceFieldSx(
                                  { width: '100%' },
                                  confidenceTone
                                )
                              : { width: '100%' },
                            helperText: error?.message
                              ? translate(error?.message)
                              : hasRequiredError
                                ? translate('errors.required')
                                : (confidenceMessage?.text ?? ''),
                            FormHelperTextProps: {
                              sx: {
                                color: confidenceTone
                                  ? CONFIDENCE_STATUS_COLORS[confidenceTone]
                                      .text
                                  : undefined
                              }
                            },
                            name: InvoiceVerificationFieldName.InvoiceDate,
                            onClick: () => setInvoiceDateOpen(true)
                          },
                          openPickerButton: {
                            onClick: () => setInvoiceDateOpen(true)
                          }
                        }}
                      />
                    </FormControl>
                  );
                }}
              />

              <Controller
                name={InvoiceVerificationFieldName.DueDate}
                rules={{
                  validate: (value) => {
                    const date = moment(
                      value,
                      DATE_FORMATS.displayedDateFormat,
                      true
                    );
                    const dateValue = date.isValid() ? date.toDate() : null;
                    const error = validateDateValue(dateValue, false);
                    if (error) return error;
                    const invoiceDateValue = getValues(
                      InvoiceVerificationFieldName.InvoiceDate
                    );
                    if (invoiceDateValue && date.isValid()) {
                      const invoiceDate = moment(
                        invoiceDateValue,
                        DATE_FORMATS.displayedDateFormat,
                        true
                      );
                      if (
                        invoiceDate.isValid() &&
                        date.isBefore(invoiceDate, 'day')
                      ) {
                        return 'errors.dueDateBeforeInvoiceDate';
                      }
                    }
                    return true;
                  }
                }}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error, isDirty }
                }) => {
                  const { isLow, isMedium, confidenceMessage } =
                    getFieldConfidenceUi(
                      currentInvoice,
                      InvoiceVerificationFieldName.DueDate,
                      translate,
                      isDirty
                    );
                  const confidenceTone = !!error
                    ? null
                    : isLow
                      ? 'lowConfidence'
                      : isMedium
                        ? 'needsReview'
                        : null;
                  const showError = !!error || isLow;
                  const isWarning = confidenceTone === 'needsReview';

                  return (
                    <FormControl
                      error={showError}
                      color={isWarning ? 'warning' : undefined}
                      sx={verificationFormItem}
                    >
                      <DatePicker
                        sx={{ width: '100%' }}
                        format={DATE_FORMATS.displayedDateFormat}
                        open={isDueDateOpen}
                        label={translate('labels.dueDate')}
                        value={
                          value
                            ? moment(value, DATE_FORMATS.displayedDateFormat)
                            : null
                        }
                        onClose={() => setDueDateOpen(false)}
                        onChange={(value) => {
                          const dateValue = value as Moment | null;
                          if (!dateValue?.isValid()) {
                            onChange('');
                          } else {
                            onChange(
                              dateValue.format(DATE_FORMATS.displayedDateFormat)
                            );
                          }
                          void trigger(
                            InvoiceVerificationFieldName.InvoiceDate
                          );
                        }}
                        slotProps={{
                          textField: {
                            error: showError,
                            color: isWarning ? 'warning' : undefined,
                            sx: confidenceTone
                              ? getConfidenceFieldSx(
                                  { width: '100%' },
                                  confidenceTone
                                )
                              : { width: '100%' },
                            helperText: error?.message
                              ? translate(error?.message)
                              : (confidenceMessage?.text ?? ''),
                            FormHelperTextProps: {
                              sx: {
                                color: confidenceTone
                                  ? CONFIDENCE_STATUS_COLORS[confidenceTone]
                                      .text
                                  : undefined
                              }
                            },
                            name: InvoiceVerificationFieldName.DueDate,
                            onClick: () => setDueDateOpen(true)
                          },
                          openPickerButton: {
                            onClick: () => setDueDateOpen(true)
                          }
                        }}
                      />
                    </FormControl>
                  );
                }}
              />
            </Box>
          )}

          {!isRevenue && documentConfig.allowExpenseType && (
            <Box
              sx={{
                ...verificationFormFieldsWrapper,
                minHeight: { xs: '200px', sm: '70px' },
                flexDirection: { xs: 'column', sm: 'row' }
              }}
            >
              <Controller
                name={InvoiceVerificationFieldName.ExpenseType}
                control={control}
                rules={{
                  required: documentConfig.allowExpenseType
                    ? translate('errors.expenseType')
                    : false
                }}
                render={({ field, fieldState: { error } }) => {
                  const selectedExpenseType = expenseTypeOptions.find(
                    (expenseType) =>
                      String(expenseType.id) === String(field.value)
                  );
                  const isSelectedDeactivated = Boolean(
                    selectedExpenseType && !selectedExpenseType.isActive
                  );
                  const hasActiveOptions = expenseTypeOptions.some(
                    (expenseType) => expenseType.isActive
                  );
                  const isEmpty = !field.value;
                  const showError =
                    !isSelectedDeactivated && (!!error || isEmpty);

                  return (
                    <FormControl error={showError} sx={verificationFormItem}>
                      <InputLabel id="expense-type-select-label">{`${translate(
                        'labels.expenseType'
                      )}*`}</InputLabel>
                      <Select
                        {...field}
                        onChange={(event) => {
                          const selected = (event.target as HTMLInputElement)
                            .value as string;
                          // AC11: re-selecting the current type is a no-op
                          if (String(selected) === String(field.value || '')) {
                            return;
                          }
                          // AC12: a different type starts the bulk update flow
                          setPendingExpenseTypeChange(selected);
                        }}
                        id="expense-type-select"
                        labelId="expense-type-select-label"
                        value={field.value || ''}
                        label={`${translate('labels.expenseType')}*`}
                        error={showError}
                        disabled={isSelectedDeactivated && !hasActiveOptions}
                        renderValue={(selected) => {
                          const option = expenseTypeOptions.find(
                            (expenseType) =>
                              String(expenseType.id) === String(selected)
                          );
                          if (!option) {
                            return '';
                          }

                          return (
                            <span
                              style={{
                                color: option.isActive ? 'inherit' : 'gray'
                              }}
                            >
                              {option.isActive
                                ? option.name
                                : `${option.name} ${translate(
                                    'labels.deactivatedExpenseTypeValue'
                                  )}`}
                            </span>
                          );
                        }}
                        sx={{ textAlign: 'left' }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 350
                            }
                          }
                        }}
                      >
                        {expenseTypeOptions.map(
                          (expenseType: InvoiceExpenseType) =>
                            expenseType.isActive && (
                              <MenuItem
                                key={expenseType.id}
                                value={expenseType.id}
                              >
                                {expenseType.name}
                              </MenuItem>
                            )
                        )}
                      </Select>
                      <FormHelperText error={showError}>
                        {showError ? translate('errors.required') : ''}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            </Box>
          )}

          {!isNoDocumentType && (
            <Box>
              <Box
                sx={{
                  ...verificationFormFieldsWrapper,
                  minHeight: '40px',
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 3, sm: 2 },
                    width: '100%'
                  }}
                >
                  <Controller
                    name={InvoiceVerificationFieldName.VatBase}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState: { error, isDirty } }) => {
                      const { isLow, isMedium, confidenceMessage } =
                        getFieldConfidenceUi(
                          currentInvoice,
                          InvoiceVerificationFieldName.VatBase,
                          translate,
                          isDirty
                        );
                      const isEmpty = !field.value;
                      const hasRequiredError = !!error || isEmpty;
                      const confidenceTone = hasRequiredError
                        ? null
                        : isLow
                          ? 'lowConfidence'
                          : isMedium
                            ? 'needsReview'
                            : null;
                      const showError = hasRequiredError || isLow;
                      const isWarning = confidenceTone === 'needsReview';

                      return (
                        <TextField
                          {...field}
                          onChange={(event) => {
                            field.onChange(
                              processMoneyInput(
                                event.target.value,
                                String(field.value)
                              )
                            );
                            void trigger(
                              InvoiceVerificationFieldName.InvoiceAmount
                            );
                          }}
                          onBlur={() => {
                            field.onChange(formatToTwoDecimals(field.value));
                            field.onBlur();
                          }}
                          error={showError}
                          color={isWarning ? 'warning' : undefined}
                          label={`${translate('labels.vatBase')}*`}
                          name={InvoiceVerificationFieldName.VatBase}
                          placeholder={translate('labels.vatBase')}
                          sx={
                            confidenceTone
                              ? getConfidenceFieldSx(
                                  verificationFormItemNoHeight,
                                  confidenceTone
                                )
                              : verificationFormItemNoHeight
                          }
                          helperText={
                            hasRequiredError
                              ? translate('errors.required')
                              : (confidenceMessage?.text ?? '')
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: confidenceTone
                                ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                                : undefined
                            }
                          }}
                        />
                      );
                    }}
                  />
                  <Controller
                    name={InvoiceVerificationFieldName.VatAmount}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState: { error, isDirty } }) => {
                      const { isLow, isMedium, confidenceMessage } =
                        getFieldConfidenceUi(
                          currentInvoice,
                          InvoiceVerificationFieldName.VatAmount,
                          translate,
                          isDirty
                        );
                      const isEmpty = !field.value;
                      const hasRequiredError = !!error || isEmpty;
                      const confidenceTone = hasRequiredError
                        ? null
                        : isLow
                          ? 'lowConfidence'
                          : isMedium
                            ? 'needsReview'
                            : null;
                      const showError = hasRequiredError || isLow;
                      const isWarning = confidenceTone === 'needsReview';

                      return (
                        <TextField
                          {...field}
                          onChange={(event) => {
                            field.onChange(
                              processMoneyInput(
                                event.target.value,
                                String(field.value)
                              )
                            );
                            void trigger(
                              InvoiceVerificationFieldName.InvoiceAmount
                            );
                          }}
                          onBlur={() => {
                            field.onChange(formatToTwoDecimals(field.value));
                            field.onBlur();
                          }}
                          error={showError}
                          color={isWarning ? 'warning' : undefined}
                          label={`${translate('labels.vatAmount')}*`}
                          name={InvoiceVerificationFieldName.VatAmount}
                          placeholder={translate('labels.vatAmount')}
                          sx={
                            confidenceTone
                              ? getConfidenceFieldSx(
                                  verificationFormItemNoHeight,
                                  confidenceTone
                                )
                              : verificationFormItemNoHeight
                          }
                          helperText={
                            hasRequiredError
                              ? translate('errors.required')
                              : (confidenceMessage?.text ?? '')
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: confidenceTone
                                ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                                : undefined
                            }
                          }}
                        />
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 3, sm: 2 },
                    width: '100%'
                  }}
                >
                  <Controller
                    name={InvoiceVerificationFieldName.InvoiceAmount}
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => validateInvoiceAmount(value)
                    }}
                    render={({ field, fieldState: { error, isDirty } }) => {
                      const { isLow, isMedium, confidenceMessage } =
                        getFieldConfidenceUi(
                          currentInvoice,
                          InvoiceVerificationFieldName.InvoiceAmount,
                          translate,
                          isDirty
                        );
                      const isEmpty = !field.value;
                      const hasFieldError =
                        shouldShowInvoiceAmountError || !!error || isEmpty;
                      const confidenceTone = hasFieldError
                        ? null
                        : isLow
                          ? 'lowConfidence'
                          : isMedium
                            ? 'needsReview'
                            : null;
                      const showError = hasFieldError || isLow;
                      const isWarning = confidenceTone === 'needsReview';

                      return (
                        <TextField
                          {...field}
                          onChange={(event) => {
                            field.onChange(
                              processMoneyInput(
                                event.target.value,
                                String(field.value)
                              )
                            );
                          }}
                          onBlur={() => {
                            field.onChange(formatToTwoDecimals(field.value));
                            field.onBlur();
                          }}
                          label={`${translate('labels.invoiceAmount')}*`}
                          name={InvoiceVerificationFieldName.InvoiceAmount}
                          error={showError}
                          color={isWarning ? 'warning' : undefined}
                          placeholder={translate('labels.invoiceAmount')}
                          sx={
                            confidenceTone
                              ? getConfidenceFieldSx(
                                  verificationFormItemNoHeight,
                                  confidenceTone
                                )
                              : verificationFormItemNoHeight
                          }
                          helperText={
                            error?.message
                              ? translate(String(error.message))
                              : shouldShowInvoiceAmountError
                                ? translate('errors.invoiceAmountEqual')
                                : hasFieldError
                                  ? translate('errors.required')
                                  : (confidenceMessage?.text ?? '')
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: confidenceTone
                                ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                                : undefined
                            }
                          }}
                        />
                      );
                    }}
                  />
                  <Controller
                    name={InvoiceVerificationFieldName.CurrencyId}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState: { error, isDirty } }) => {
                      const { isLow, isMedium, confidenceMessage } =
                        getFieldConfidenceUi(
                          currentInvoice,
                          InvoiceVerificationFieldName.CurrencyId,
                          translate,
                          isDirty
                        );
                      const isEmpty = !field.value;
                      const hasRequiredError = !!error || isEmpty;
                      const confidenceTone = hasRequiredError
                        ? null
                        : isLow
                          ? 'lowConfidence'
                          : isMedium
                            ? 'needsReview'
                            : null;
                      const showError = hasRequiredError || isLow;
                      const isWarning = confidenceTone === 'needsReview';

                      return (
                        <FormControl
                          error={showError}
                          color={isWarning ? 'warning' : undefined}
                          sx={
                            confidenceTone
                              ? getConfidenceFieldSx(
                                  verificationFormItemNoHeight,
                                  confidenceTone
                                )
                              : verificationFormItemNoHeight
                          }
                        >
                          <InputLabel id="currency-select-label">{`${translate(
                            'labels.currency'
                          )}*`}</InputLabel>
                          <Select
                            {...field}
                            id="currency-select"
                            labelId="currency-select-label"
                            sx={
                              confidenceTone
                                ? getConfidenceFieldSx(
                                    { textAlign: 'left' },
                                    confidenceTone
                                  )
                                : { textAlign: 'left' }
                            }
                            key={`${watchedInvoiceDate}`}
                            error={showError}
                            color={isWarning ? 'warning' : undefined}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 350
                                }
                              }
                            }}
                            label={`${translate('labels.currency')}*`}
                          >
                            {filteredCurrencies.map(
                              (currency: CurrencyDropDown) => (
                                <MenuItem key={currency.id} value={currency.id}>
                                  {currency.isoCode}
                                </MenuItem>
                              )
                            )}
                          </Select>
                          <FormHelperText
                            error={showError}
                            sx={{
                              color: confidenceTone
                                ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                                : undefined
                            }}
                          >
                            {hasRequiredError
                              ? translate('errors.required')
                              : (confidenceMessage?.text ?? '')}
                          </FormHelperText>
                        </FormControl>
                      );
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        <Modal
          headerTitle={translate('messages.applyExpenseTypeTitle')}
          isOpen={pendingExpenseTypeChange !== null}
          hide={() => setPendingExpenseTypeChange(null)}
          maxWidth="sm"
        >
          <Typography variant="body1" mb={2}>
            {translate('messages.applyExpenseTypeToAllStart')}{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {expenseTypeOptions.find(
                (expenseType) =>
                  String(expenseType.id) === String(pendingExpenseTypeChange)
              )?.name ?? ''}
            </Box>{' '}
            {translate('messages.applyExpenseTypeToAllEnd')}
          </Typography>
          <Box sx={modalButtonsWrapper}>
            <Button
              variant="outlined"
              onClick={() => setPendingExpenseTypeChange(null)}
              data-testid="document-data-expense-type-cancel"
            >
              {translate('buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmExpenseTypeChange}
              data-testid="document-data-expense-type-apply"
            >
              {translate('buttons.apply')}
            </Button>
          </Box>
        </Modal>
      </LocalizationProvider>
    </InvoiceVerificationCard>
  );
};
